import { GridStack, Loader } from 'component'
import { modifier, module } from 'constant'
import { useEvent, useState } from 'hooks'
import { resizerSelectors } from 'selectors'
import { cn } from 'utils'
import { constant } from '@/component/gridstack/constant'
import { getFlipWrapperSelectors } from '@/selectors/resizer'

export type Sizes = {
	[key: string]: {
		w?: number
		h?: number
		parent?: string
		flip?: boolean
	}
}

export type Parents = {
	[key: string]: {
		w?: number
		h?: number
	}
}

type InitProps = {
	onInit?: () => void
	onResizeActiveChange?: (active: boolean) => void
	onAutoHeight?: (newSizes: Sizes) => void
	onReinit?: () => void
	onActivate?: () => void
	onDeactivate?: () => void
}

const {
	resizer: { selector, style },
} = module

const GRID_COL_HEIGHT = 40
const GRID_COLS = 24
const HEIGHT_GRID_COLS = 40
const CLASS_PANEL_RIGHT = 'blocks-resizer__panel-right'
const CLASS_PANEL_BOTTOM = 'blocks-resizer__panel-bottom'
const CLASS_LINES_WRAP = 'blocks-resizer__lines-wrap'
const CLASS_LINES = 'blocks-resizer__lines'
const CLASS_LINE = 'blocks-resizer__line'
const CLASS_ROW_LINE = 'blocks-resizer__row-line'
const SELECTOR_CLASS_PANEL_RIGHT = cn.removeDot(selector.rightPanel)
const SELECTOR_CLASS_PANEL_BOTTOM = cn.removeDot(selector.bottomPanel)

const makeWidths = () => {
	const widths: number[] = []
	for (let i = 1; i <= GRID_COLS; i++) {
		widths.push((100 / GRID_COLS) * i)
	}
	return widths
}

const makeHeights = () => {
	const heights: number[] = []
	for (let i = 1; i <= HEIGHT_GRID_COLS; i++) {
		heights.push(GRID_COL_HEIGHT * i)
	}
	return heights
}

export const Resize = () => {
	const { state, setState } = useState({
		active: false,
		widths: makeWidths(),
		heights: makeHeights(),
		sizes: {} as Sizes,
		autoHeightsComplete: false,
	})

	const destroyResizerLoading = () => {
		const wrap = resizerSelectors.getWrapsSelector()
		const loaders = resizerSelectors.getLoaders()

		if (!wrap || !loaders) return

		for (let i = 0, { length } = loaders; i < length; i++) {
			const loader = loaders[i]
			const { parentNode } = loader

			if (!parentNode) continue
			parentNode.removeChild(loader)
		}

		for (let i = 0, { length } = wrap; i < length; i++) {
			cn.removeClass(wrap[i], modifier.hidden)
		}
	}

	const makeLoading = () => {
		const { containers } = state

		if (!containers) return
		const { length } = containers

		if (length === 0) return
		for (let i = 0; i < length; i++) {
			const container = containers[i]
			const fragment = document.createDocumentFragment()
			const loading = document.createElement('div')
			cn.addClass(loading, style.loading)
			cn.addClass(loading, cn.removeDot(selector.loading))

			fragment.appendChild(loading)
			container.appendChild(fragment)
			Loader.create(container, { absolute: true })

			cn.addClass(loading, modifier.active)
		}
	}

	const destroyLoading = () => {
		destroyResizerLoading()

		const loadings = resizerSelectors.getLoadings()
		if (!loadings) {
			return
		}

		const { length } = loadings
		if (length === 0) {
			return
		}

		for (let i = 0; i < length; i++) {
			const loading = loadings[i] as HTMLElement
			if (!loading) {
				continue
			}

			const { parentNode } = loading
			if (!parentNode) {
				continue
			}

			cn.removeClass(loading, modifier.active)
			setTimeout(() => {
				if (!loading) {
					return
				}
				if (loading.parentNode) {
					loading.parentNode.removeChild(loading)
				}
			}, 200)
		}

		Loader.destroyAll()
	}

	const makeItemCover = () => {
		const { items } = state

		if (!items) return
		const { length } = items

		if (length === 0) return
		for (let i = 0; i < length; i++) {
			const item = items[i]

			if (!item) continue
			const block = resizerSelectors.getItemBlock(item)

			if (!block) continue
			const fragment = document.createDocumentFragment()
			const cover = document.createElement('div')

			cn.addClass(cover, cn.removeDot(selector.itemCover))
			cn.addClass(cover, style.itemCover)

			fragment.appendChild(cover)
			block.appendChild(fragment)
		}
	}

	const destroyItemCover = () => {
		const { items } = state

		if (!items) return
		const { length } = items

		if (length === 0) return
		for (let i = 0; i < length; i++) {
			const item = items[i]

			if (!item) continue
			const cover = resizerSelectors.getItemCover(item)

			if (!cover) continue
			const { parentNode } = cover

			if (!parentNode) return
			parentNode.removeChild(cover)
		}
	}

	const makeLines = () => {
		const fragment = document.createDocumentFragment()
		const linesWrap = document.createElement('div')
		const lines = document.createElement('ul')
		const rowLinesWrap = document.createElement('div')
		const rowLines = document.createElement('ul')

		cn.addClass(linesWrap, CLASS_LINES_WRAP)
		cn.addClass(linesWrap, cn.removeDot(selector.lines))
		cn.addClass(lines, CLASS_LINES)

		cn.addClass(rowLinesWrap, CLASS_LINES_WRAP)
		cn.addClass(rowLinesWrap, cn.removeDot(selector.lines))
		cn.addClass(rowLines, CLASS_LINES)
		cn.addClass(rowLines, modifier.row)

		for (let i = 0; i < GRID_COLS - 1; i++) {
			const line = document.createElement('li')
			cn.addClass(line, CLASS_LINE)
			lines.appendChild(line)
		}

		for (let i = 0; i < HEIGHT_GRID_COLS - 1; i++) {
			const rowLine = document.createElement('li')
			cn.addClass(rowLine, CLASS_ROW_LINE)
			rowLines.appendChild(rowLine)
		}

		linesWrap.appendChild(lines)
		rowLinesWrap.appendChild(rowLines)

		fragment.appendChild(linesWrap)
		fragment.appendChild(rowLinesWrap)

		return fragment
	}

	const destroyLines = () => {
		const lines = resizerSelectors.getLines()

		if (!lines) return
		const { length } = lines

		if (length === 0) return
		for (let i = 0; i < length; i++) {
			const item = lines[i]
			const parent = item.parentNode as HTMLElement

			if (!parent) continue
			parent.removeChild(item)
		}
	}

	const handleLines = () => {
		const { containers, active } = state

		if (!containers) return
		const { length } = containers

		if (length === 0) return
		if (!active) {
			destroyLines()
			return
		}
		for (let i = 0; i < length; i++) {
			const container = containers[i]
			container.appendChild(makeLines())
		}
	}

	const makeItemsPanels = (scope: HTMLElement) => {
		const fragment = document.createDocumentFragment()
		const rightPanel = document.createElement('div')
		const bottomPanel = document.createElement('div')

		cn.addClass(rightPanel, CLASS_PANEL_RIGHT)
		cn.addClass(rightPanel, SELECTOR_CLASS_PANEL_RIGHT)

		cn.addClass(bottomPanel, CLASS_PANEL_BOTTOM)
		cn.addClass(bottomPanel, SELECTOR_CLASS_PANEL_BOTTOM)

		fragment.appendChild(rightPanel)
		fragment.appendChild(bottomPanel)

		scope.appendChild(fragment)
	}

	const destroyPanels = () => {
		const rightPanels = resizerSelectors.getRightPanels()
		const bottomPanels = resizerSelectors.getBottomPanels()

		if (!rightPanels || !bottomPanels) return
		const rightPanelsLength = rightPanels.length
		const bottomPanelsLength = bottomPanels.length

		if (rightPanelsLength === 0) return
		for (let i = 0; i < rightPanelsLength; i++) {
			const rightPanel = rightPanels[i]
			const parent = rightPanel.parentNode as HTMLElement

			if (!parent) continue
			parent.removeChild(rightPanel)
		}

		if (bottomPanelsLength === 0) return
		for (let i = 0; i < bottomPanelsLength; i++) {
			const bottomPanel = bottomPanels[i]
			const parent = bottomPanel.parentNode as HTMLElement

			if (!parent) continue
			parent.removeChild(bottomPanel)
		}
	}

	const handleActivateResizeEvents = (el: HTMLElement) => {
		const click = useEvent(el, 'click')
		const { onResizeActiveChange, onActivate, onDeactivate } = state

		if (state.active) {
			activateWrapperChildren(false)
		}

		click.register(() => {
			if (state.active) {
				activateWrapperChildren(true)
				GridStack.disableResizing()
				setState({ active: false })
				cn.removeClass(el, modifier.active)
				if (onDeactivate) {
					onDeactivate()
				}
			} else {
				activateWrapperChildren(false)
				GridStack.enableResizing()
				makeItemCover()
				setState({ active: true })
				cn.addClass(el, modifier.active)
				if (onActivate) {
					onActivate()
				}
			}

			if (onResizeActiveChange) {
				onResizeActiveChange(state.active)
			}

			handleShowItemsPanels()
			handleLines()

			if (!state.active) {
				destroyPanels()
				destroyItemCover()
			}
		})
	}

	const reinitActive = () => {
		const { onResizeActiveChange, onActivate, onReinit } = state

		makeItemCover()

		setState({ active: true })
		if (onReinit) onReinit()
		if (onActivate) onActivate()
		if (onResizeActiveChange) onResizeActiveChange(state.active)

		destroyPanels()
		handleShowItemsPanels()

		destroyLines()
		handleLines()
	}

	const handleShowItemsPanels = () => {
		const { items, active } = state

		if (!items) return
		const { length } = items

		if (length === 0) return
		for (let i = 0; i < length; i++) {
			const item = items[i]

			if (!item) continue
			if (active) {
				makeItemsPanels(item)
			}
		}
	}

	const initActivators = () => {
		const { activators } = state

		if (!activators) return
		const { length } = activators

		if (length === 0) return
		for (let i = 0; i < length; i++) {
			const activator = activators[i]

			if (!activator) continue
			handleActivateResizeEvents(activator)
		}
	}

	const autoHeights = async (oldSizes: any) => {
		const { onAutoHeight } = state
		const oldKeys = Object.keys(oldSizes)
		const keys = Object.keys(state.sizes)
		const newKeys = keys.filter((item) => oldKeys.indexOf(item) < 0)

		initSizes()
		if (onAutoHeight && newKeys.length > 0) onAutoHeight(getSizesByKeys(newKeys))
	}

	const getActualSizesFromDOM = () => {
		const sizes: Sizes = {}
		const items = resizerSelectors.getItems()

		if (!items) return sizes
		const { length } = items

		if (length === 0) return sizes

		for (let i = 0; i < length; i++) {
			const item = items[i]
			const id = item.getAttribute(selector.itemId)
			const flip = item.getAttribute(selector.flip)?.toLowerCase() === 'true'
			const parent = item.getAttribute(selector.parentId)
			const w = item.getAttribute(constant.selector.width)
			const h = item.getAttribute(constant.selector.height)

			if (!id) continue
			if (w) {
				sizes[id] = { w: parseFloat(w) }
			}

			if (h) {
				sizes[id] = { ...sizes[id], h: parseFloat(h) }
			}

			if (parent) {
				sizes[id] = { ...sizes[id], parent }
			}

			if (flip) {
				sizes[id] = { ...sizes[id], flip }
			}
		}

		return sizes
	}

	const activateWrapperChildren = (activate: boolean) => {
		const { items } = state
		if (!items) return
		for (let i = 0, { length } = items; i < length; i++) {
			const item = items[i]
			if (!cn.hasClass(item, selector.hasWrapper)) {
				continue
			}
			if (activate) {
				cn.removeClass(item, modifier.disabled)
			} else {
				cn.addClass(item, modifier.disabled)
			}
		}
	}

	const initControlKey = () => {
		const keyDown = useEvent<KeyboardEvent>(document, 'keydown')
		const keyUp = useEvent<KeyboardEvent>(document, 'keyup')
		let pressed = false

		keyDown.register(({ e }) => {
			if (!state.active) return
			if (e.key === 'Control' || e.key === 'Meta' || !pressed) {
				pressed = true
				activateWrapperChildren(true)
			}
		})

		keyUp.register(({ e }) => {
			if (!state.active) return
			if (e.key === 'Control' || e.key === 'Meta' || pressed) {
				pressed = false
				activateWrapperChildren(false)
			}
		})
	}

	const initSizes = () => {
		setState({ sizes: getActualSizesFromDOM() })
	}

	const getSizesByKeys = (keys: string[]) => {
		if (!keys) return {}
		const { sizes } = state
		const { length } = keys
		const result: Sizes = {}

		if (length === 0) return {}
		for (let i = 0; i < length; i++) {
			const key = keys[i]

			if (!key || !sizes[key]) continue
			result[key] = sizes[key]
		}

		return result
	}

	const fixFlippersPosition = () => {
		const flipWrappers = getFlipWrapperSelectors()
		if (!flipWrappers) return

		const { length } = flipWrappers
		for (let i = 0; i < length; i++) {
			const flipWrapper = flipWrappers[i]
			if (!flipWrapper) {
				continue
			}
			const items = flipWrapper.querySelectorAll(constant.selector.item)
			let flipWrapperHeight = null
			const itemsLength = items.length
			for (let j = 0; j < itemsLength; j++) {
				const item = items[j]
				if (!item) {
					continue
				}
				if (flipWrapperHeight === null) {
					flipWrapperHeight = Math.ceil(Number(item.getAttribute(constant.selector.height)) * GRID_COL_HEIGHT)
				}
				item.setAttribute(constant.selector.y, '0')
			}
			flipWrapper.style.height = `${flipWrapperHeight}px`
		}
	}

	const getSizes = () => state.sizes

	const getActive = () => state.active

	const init = (props: InitProps) => {
		setState({
			...resizerSelectors.getResizerSelectors(),
			...props,
		})

		const { sizes } = state

		initSizes()
		initActivators()
		initControlKey()

		if (props.onInit) props.onInit()

		if (state.active) {
			destroyItemCover()
			autoHeights(sizes)
			reinitActive()
		}
		fixFlippersPosition()
	}

	return {
		init,
		getSizes,
		getActive,
		initSizes,
		reinitActive,
		makeLoading,
		destroyLoading,
		destroyResizerLoading,
		fixFlippersPosition,
	}
}
